import { SelectField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { Trans, useApplicationContext } from '~/common/context';
import {
  Button,
  Flex,
  FormContainer,
  Icon,
  IconButton,
  Modal,
  notify,
  Table,
  Text,
  Title,
  Alert,
  Card,
  Col,
  Container,
  Row,
} from '@wyz/ui';
import { useForm, useWatch } from 'react-hook-form';
import { useMyCustomers } from '~/common/hooks';
import { useManageQuoteSetting } from '~/purchase/api';
import { invariant } from '@wyz/utils';
import { useManageUsers } from '~/common/api';

type Props = {
  onClose: () => void;
};

function DuplicateQuoteSettingsModal({ onClose }: Props) {
  const methods = useForm<{ customers: string[] }>();
  const customersByUser = useMyCustomers();
  const { auth } = useApplicationContext();
  const { state, handlers } = usePurchaseContext();
  const selectedCustomer = state.targetCustomer;
  invariant(selectedCustomer);

  const watchedCustomers =
    useWatch({ name: 'customers', control: methods.control }) ?? [];

  const manageQuoteSettings = useManageQuoteSetting({
    list: {
      enabled: watchedCustomers.length > 0,
      args: { customers: watchedCustomers },
    },
    duplicate: {
      enabled: true,
      onSuccess: () => {
        notify.success(<Trans i18nKey='app.common.messages.success' />, {
          id: 'toast-success-quote-settings-duplicated',
        });
        onClose();
      },
    },
  });

  const existingSettings = (manageQuoteSettings.list.data?.data ?? []).filter(
    (quoteSetting) => watchedCustomers.includes(quoteSetting.customerId),
  );

  const manageUserCustomers = useManageUsers({
    paginateCustomersByUser: {
      args: {
        userId: auth.userId as string,
        ids: watchedCustomers,
      },
      enabled: !!watchedCustomers.length,
      refetchOnMount: true,
    },
  });

  return (
    <FormContainer
      id='duplicate-form'
      methods={methods}
      submitHandler={({ customers }) =>
        manageQuoteSettings.duplicate.mutate({ customerIds: customers })
      }
    >
      <Modal.Container onClose={onClose} open={true} size='xl'>
        <Modal.Body>
          <Title heading='xl' weight='bold'>
            <Trans i18nKey='app.purchase.quote_settings.duplicate.title' />
          </Title>
          <Container>
            <Row className='mt-5 mb-4'>
              <Col xs={7}>
                <Card>
                  <Card.Body>
                    <SelectField
                      isLoading={customersByUser.isLoading}
                      options={(customersByUser.data?.data ?? [])
                        .filter(
                          (customer) => customer.id !== selectedCustomer.id,
                        )
                        .map((customer) => ({
                          label: `${customer.name} (${customer.code})`,
                          value: customer.id,
                        }))}
                      name='customers'
                      label={
                        <Trans i18nKey='app.purchase.quote_settings.duplicate.customers' />
                      }
                      required
                      isClearable
                      isMulti
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {existingSettings.length > 0 &&
              manageUserCustomers.paginateCustomersByUser.isSuccess && (
                <Card>
                  <Card.Body>
                    <Alert
                      variant='warning'
                      className='d-flex align-items-center'
                    >
                      <Icon
                        icon='alert'
                        size='md'
                        className='text-warning-text me-3'
                      />
                      <Text heading='sm' color='warning'>
                        <Trans i18nKey='app.purchase.quote_settings.duplicate.customers_warning' />
                      </Text>
                    </Alert>
                    <Table>
                      <tr>
                        <th>
                          <Trans i18nKey='app.purchase.quote_settings.duplicate.table.customer' />
                        </th>
                        <th>
                          <Trans i18nKey='app.common.button.visit' />
                        </th>
                      </tr>

                      {existingSettings.map((settings) => {
                        const customer =
                          manageUserCustomers.paginateCustomersByUser.data?.data.find(
                            (customer) => customer.id === settings.customerId,
                          );
                        invariant(customer);
                        return (
                          <tr key={settings.customerId}>
                            <td>{customer.name}</td>
                            <td>
                              <IconButton
                                id='display-settings'
                                icon='eye'
                                variant='tertiary-blue'
                                onClick={() => {
                                  onClose();
                                  handlers.setPrimaryCustomerId(customer.id);
                                }}
                              >
                                <Trans i18nKey='app.common.button.see' />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })}
                    </Table>
                  </Card.Body>
                </Card>
              )}
          </Container>
          <Flex justifyContent='end' className='mt-5'>
            <Card>
              <Card.Body>
                <Flex className='gap-2'>
                  <Button
                    id='cancel-duplicate'
                    variant='secondary'
                    onClick={onClose}
                  >
                    <Trans i18nKey='app.common.button.cancel' />
                  </Button>
                  <Button
                    id='apply-duplicate'
                    variant='primary'
                    type='submit'
                    form='duplicate-form'
                    isLoading={manageQuoteSettings.duplicate.isLoading}
                    disabled={watchedCustomers.length === 0}
                  >
                    <Trans i18nKey='app.common.button.apply' />
                  </Button>
                </Flex>
              </Card.Body>
            </Card>
          </Flex>
        </Modal.Body>
      </Modal.Container>
    </FormContainer>
  );
}

export default DuplicateQuoteSettingsModal;
